/**
 * Account-level watch-state sync (ARCHITECTURE §4b "watch-state sync —
 * GUID-keyed only"): the server-plane ingest DTOs, the account-facing
 * history/stats DTOs, and the down-sync page. Runtime validators behind
 * contracts/openapi/web-server-plane.v1.yaml and web-broker.v1.yaml.
 *
 * Privacy inversion (TV §7): an event carries the content GUID, the
 * account id, the event kind and time, and a display snapshot (title,
 * year, show block) — never paths, inventories, or resume offsets.
 * `local://` GUIDs never leave the server.
 */
import { z } from "zod";

export type RagoGuidType = "movie" | "show" | "season" | "episode";
export type RagoGuidSource = "tmdb" | "tvdb";

const RAGO_GUID = /^rago:\/\/(movie|show|season|episode)\/(tmdb|tvdb)-(\d{1,12})$/;

/**
 * Parse a canonical `rago://{type}/{src}-{native}` GUID (rago-metadata's
 * grammar); null for anything else (`local://…`, malformed).
 */
export function parseRagoGuid(
  guid: string,
): { type: RagoGuidType; src: RagoGuidSource; native: string } | null {
  const m = RAGO_GUID.exec(guid);
  if (!m) return null;
  const [, type, src, native] = m;
  if (!type || !src || !native) return null;
  return { type: type as RagoGuidType, src: src as RagoGuidSource, native };
}

export const watchEventKindSchema = z.enum(["scrobble", "unscrobble", "rate"]);
export type WatchEventKind = z.infer<typeof watchEventKindSchema>;

export const watchEventShowSchema = z.object({
  guid: z.string().min(1).max(200),
  title: z.string().max(500).optional(),
  season: z.number().int().min(0),
  episode: z.number().int().min(0),
});
export type WatchEventShow = z.infer<typeof watchEventShowSchema>;

/**
 * One server-side watch event. Deliberately shape-only: semantic problems
 * (guid grammar, a missing show block on an episode) are rejected per
 * event by the broker so a batch is never poisoned by one bad row.
 */
export const watchEventSchema = z.object({
  /** Server-chosen, deterministic per (guid, kind, timestamp) — the idempotency key. */
  id: z.string().min(1).max(200),
  userId: z.uuid(),
  guid: z.string().min(1).max(200),
  event: watchEventKindSchema,
  /** rate only: 0–10, null clears. */
  value: z.number().min(0).max(10).nullable().optional(),
  at: z.iso.datetime(),
  title: z.string().max(500).optional(),
  year: z.number().int().nullable().optional(),
  show: watchEventShowSchema.optional(),
  /** The item's runtime on the origin server (ms); omitted when unknown. */
  durationMs: z.number().int().min(0).optional(),
});
export type WatchEvent = z.infer<typeof watchEventSchema>;

export const watchEventsRequestSchema = z.object({
  events: z.array(watchEventSchema).min(1).max(200),
});
export type WatchEventsRequest = z.infer<typeof watchEventsRequestSchema>;

export const watchEventRejectReasonSchema = z.enum([
  "no_grant",
  "sync_off",
  "bad_guid",
  "bad_event",
  "shared_unscrobble",
]);
export type WatchEventRejectReason = z.infer<typeof watchEventRejectReasonSchema>;

export const watchEventsResultSchema = z.object({
  accepted: z.number().int().min(0),
  duplicates: z.number().int().min(0),
  rejected: z.array(z.object({ id: z.string(), reason: watchEventRejectReasonSchema })),
});
export type WatchEventsResult = z.infer<typeof watchEventsResultSchema>;

export const watchMediaTypeSchema = z.enum(["movie", "episode"]);
export type WatchMediaType = z.infer<typeof watchMediaTypeSchema>;

/** One completed viewing on the account's profile, hydrated from the Discovery cache. */
export const watchHistoryEntrySchema = z.object({
  id: z.string(),
  guid: z.string(),
  mediaType: watchMediaTypeSchema,
  title: z.string(),
  year: z.number().int().nullable(),
  showTitle: z.string().nullable(),
  showGuid: z.string().nullable(),
  seasonNumber: z.number().int().nullable(),
  episodeNumber: z.number().int().nullable(),
  watchedAt: z.iso.datetime(),
  /** Present only for tmdb-anchored GUIDs (Discovery links). */
  tmdb: z.object({ movieId: z.string().optional(), tvId: z.string().optional() }),
  /** TMDB image paths; the client builds URLs like Discovery does. */
  art: z.object({
    stillPath: z.string().nullable(),
    backdropPath: z.string().nullable(),
    posterPath: z.string().nullable(),
  }),
});
export type WatchHistoryEntry = z.infer<typeof watchHistoryEntrySchema>;

export const watchHistoryResponseSchema = z.object({
  items: z.array(watchHistoryEntrySchema),
  nextCursor: z.string().nullable(),
});
export type WatchHistoryResponse = z.infer<typeof watchHistoryResponseSchema>;

/** Plex's "watched since joining": distinct titles ever scrobbled. */
export const watchStatsSchema = z.object({
  movies: z.number().int().min(0),
  shows: z.number().int().min(0),
  episodes: z.number().int().min(0),
  /** Runtime (ms) summed over every scrobble on record — rewatches count, unknown runtimes add nothing. */
  watchedMs: z.number().int().min(0),
  since: z.iso.datetime(),
});
export type WatchStats = z.infer<typeof watchStatsSchema>;

// ---- down-sync (server-plane): the account's current state per GUID ----

export const watchStateItemSchema = z.object({
  seq: z.string(),
  userId: z.uuid(),
  guid: z.string(),
  mediaType: watchMediaTypeSchema,
  showGuid: z.string().nullable(),
  seasonNumber: z.number().int().nullable(),
  episodeNumber: z.number().int().nullable(),
  watched: z.boolean(),
  watchedAt: z.iso.datetime().nullable(),
  /** Event time of the last watched-flag change — the LWW guard on both sides. */
  stateAt: z.iso.datetime(),
  viewCount: z.number().int().min(0),
  rating: z.number().nullable(),
  ratedAt: z.iso.datetime().nullable(),
});
export type WatchStateItem = z.infer<typeof watchStateItemSchema>;

export const watchStatePageSchema = z.object({
  items: z.array(watchStateItemSchema),
  nextCursor: z.string().nullable(),
  // max(seq) visible to the server, computed before the page rows are read
  // (the grants-page idiom) so the server can never persist a cursor past a
  // row it was not sent.
  cursorLatest: z.string(),
});
export type WatchStatePage = z.infer<typeof watchStatePageSchema>;

/**
 * The account's own mark on a Discovery title (Mark as Watched on the
 * iPhone's Discover page): a film's flag; a show's episodes marked against
 * the episodes TMDB lists (specials excluded), `watched` once every one is.
 */
export const watchedStateSchema = z.object({
  watched: z.boolean(),
  episodes: z
    .object({ watched: z.number().int().min(0), total: z.number().int().min(0) })
    .nullable(),
});
export type WatchedState = z.infer<typeof watchedStateSchema>;

